import { defineStore } from "pinia"
import { ref, computed } from "vue"
import { sleep } from "@/helpers/util"
import { Capacitor } from "@capacitor/core"
import { sqlite } from "@/helpers/sqlite"

export const useDatabaseStore = defineStore(
	"database",
	() => {
		const debug = false

		const databaseName = "trackiwi-data"

		const databaseInitialized = ref(false)
		const migrated = ref(false)

		const migrationRunning = ref(false)
		const openingConnection = ref(false)
		const importRunning = ref(false)
		const resetRunning = ref(false)

		const busy = computed(() => {
			return !databaseInitialized.value || openingConnection.value || importRunning.value || migrationRunning.value || resetRunning.value
		})

		const init = async () => {
			databaseInitialized.value = false
			try {
				await migrate()
			} finally {
				// also set when the migration failed, otherwise everything waiting for the database hangs forever
				databaseInitialized.value = true
			}
		}

		const waitForInitialisation = async () => {
			while (!databaseInitialized.value) await sleep(50)
		}

		const waitForQueue = async () => {
			//while (busy.value) await sleep(50)
		}

		const reset = async () => {
			await waitForQueue()

			resetRunning.value = true
			if (debug) console.log("🛢️ database starting reset...")
			await run(`DELETE FROM positionlogs;`, [], false, true)
			await persist(true)
			await run(`DROP TABLE positionlogs;`, [], false, true)
			await run(`VACUUM;`, [], false, true)
			await closeConnection(true)
			await openConnection(true)
			await migrate()
			await sleep(200)
			await persist(true)
			await closeConnection(true)
			await openConnection(true)
			if (debug) console.log("🛢️ database reset done...")
			resetRunning.value = false
		}

		const migrate = async () => {
			if (migrationRunning.value) return
			migrationRunning.value = true

			if (debug) console.log("🛢️ database migration...")

			const connection = await openConnection(true)
			if (!connection) {
				migrationRunning.value = false
				throw new Error("database migration failed: connection could not be opened")
			}

			if (debug) console.log("🛢️ creating positionlogs table...")
			let result = await connection.run(
				`CREATE TABLE IF NOT EXISTS positionlogs (
                id INTEGER PRIMARY KEY,
                tracker_id INTEGER,
                fix_at INTEGER,
                fix_timezone INTEGER,
                latitude REAL,
                longitude REAL,
                altitude INTEGER,
                speed REAL,
                course INTEGER,
                distance INTEGER,
                motion INTEGER
				rssi INTEGER
				sat INTEGER
				battery INTEGER
				voltage INTEGER
            );`
			)

			// create combined tracker_id/fix_at index, because
			// combination is used to read the route data from db
			result &= await connection.run(`CREATE INDEX IF NOT EXISTS idx_positionlogs_tracker_fix ON positionlogs(tracker_id, fix_at);`)

			if (result.changes && result.changes.changes && result.changes.changes < 0) {
				if (debug) console.log("🛢️ creating positionlogs table failed! ❌")
				migrationRunning.value = false
				return
			}

			if (debug) console.log("🛢️ creating positionlogs table done. ✅")

			if (debug) console.log("🛢️ adding rssi and sat column to positionlogs if not existing...")
			const tableInfo = await connection.query(`PRAGMA table_info(positionlogs);`)

			if (!tableInfo.values.some((row) => row.name === "rssi")) {
				if (debug) console.log("🛢️ adding rssi column to positionlogs.")
				result &= await connection.run(`ALTER TABLE positionlogs ADD COLUMN rssi INTEGER;`)
			}

			if (!tableInfo.values.some((row) => row.name === "sat")) {
				if (debug) console.log("🛢️ adding sat column to positionlogs.")
				result &= await connection.run(`ALTER TABLE positionlogs ADD COLUMN sat INTEGER;`)
			}

			if (!tableInfo.values.some((row) => row.name === "battery")) {
				if (debug) console.log("🛢️ adding battery column to positionlogs.")
				result &= await connection.run(`ALTER TABLE positionlogs ADD COLUMN battery INTEGER;`)
			}

			if (!tableInfo.values.some((row) => row.name === "voltage")) {
				if (debug) console.log("🛢️ adding voltage column to positionlogs.")
				result &= await connection.run(`ALTER TABLE positionlogs ADD COLUMN voltage INTEGER;`)
			}

			persist()

			migrationRunning.value = false
			migrated.value = true
		}

		const openConnection = async (skipBusyCheck = false) => {
			if (!skipBusyCheck) await waitForQueue()

			if (debug) console.log("🛢️ database opening connection")

			openingConnection.value = true

			try {
				const consistency = await sqlite.checkConnectionsConsistency()
				const connected = await sqlite.isConnection(databaseName, false)

				if (debug) console.log("🛢️ checking connection. consistency: " + consistency.result + ", connected: " + connected.result)

				let connection
				if (consistency.result && connected.result) {
					if (debug) console.log("🛢️ database connected already, using existing connection.")
					connection = await sqlite.retrieveConnection(databaseName, false)
				} else {
					if (debug) console.log("🛢️ connecting to database")
					connection = await sqlite.createConnection(databaseName, false, "no-encryption", 1, false)
				}
				const databaseIsOpen = await connection.isDBOpen()
				if (!databaseIsOpen.result) {
					if (debug) console.log("🛢️ database not open, opening...")
					await connection.open()
					if (debug) console.log("🛢️ database opened")
				}
				if (debug) console.log("🛢️ connected ✅")
				openingConnection.value = false
				return connection
			} catch (e) {
				if (debug) console.log("🛢️ database connection failed ❌")
				console.log(e)
				openingConnection.value = false
				return false
			}
		}

		const closeConnection = async (skipBusyCheck = false) => {
			if (!skipBusyCheck) await waitForQueue()

			const connected = (await sqlite.isConnection(databaseName, false)).result
			if (connected) {
				await sqlite.closeConnection(databaseName, false)
				if (debug) console.log("🛢️ database connection closed")
			} else {
				if (debug) console.log("🛢️ no database connection to close")
			}
			openingConnection.value = false
			return true
		}

		const run = async (querySql, values, transaction = true, skipBusyCheck = false) => {
			if (!skipBusyCheck) await waitForQueue()

			const connection = await openConnection(skipBusyCheck)
			if (!connection) return false

			if (debug) console.log("🛢️ database RUN: " + querySql)
			if (debug && values) console.log("🛢️ with values: " + values?.toString())

			const result = await connection.run(querySql, values, transaction)
			if (debug) console.log("🛢️ RUN complete: " + querySql)
			if (debug) console.log("🛢️ result: ", result)

			return result
		}

		const query = async (querySql, values) => {
			await waitForQueue()

			const connection = await openConnection()
			if (!connection) return false

			if (debug) console.log("🛢️ database QUERY: " + querySql)
			if (debug && values) console.log("🛢️ with values: " + values?.toString())

			const result = await connection.query(querySql, values)
			if (debug) console.log("🛢️ QUERY complete: " + querySql)
			if (debug) console.log("🛢️ result: ", result)

			return result
		}

		const batchInsert = async (table, columns, values, batchSize = 1000) => {
			await waitForQueue()

			const connection = await openConnection()
			if (!connection) return false

			const numColumns = columns.length
			const queryColumns = columns.join(", ")
			const placeholdersPerRow = `(${Array(numColumns).fill("?").join(", ")})`

			let inserted = 0

			for (let i = 0; i < values.length; i += batchSize) {
				const chunk = values.slice(i, i + batchSize)
				const queryPlaceholders = chunk.map(() => placeholdersPerRow).join(", ")
				const flatValues = chunk.flat()
				const sql = `INSERT INTO ${table} (${queryColumns}) VALUES ${queryPlaceholders}`
				await connection.run(sql, flatValues)
				inserted += chunk.length
			}

			return inserted
		}

		const importJson = async (tablesData) => {
			await waitForQueue()

			if (importRunning.value) return false

			if (debug) console.log("🛢️ import json... " + JSON.parse(tablesData).length + " tables")

			try {
				importRunning.value = true
				const importJson = `{
					"database": "${databaseName}",
					"version": 1,
					"encrypted": false,
					"mode": "partial",
					"tables": ${tablesData}
				}`

				if (debug) console.log("🛢️ staring import. ")

				await closeConnection(true)
				const result = await sqlite.importFromJson(importJson)

				importRunning.value = false

				if (debug) console.log("🛢️ import done. ✅")

				return result
			} catch (e) {
				console.error("🛢️ import failed with exception ❌")
				console.error(e)
			}
			importRunning.value = false
			return false
		}

		const persist = async (skipBusyCheck = false) => {
			if (Capacitor.getPlatform() !== "web") {
				return
			}
			await waitForInitialisation()
			console.log("💾 staring to persist database.")
			await openConnection(skipBusyCheck)
			await sqlite.saveToStore(databaseName)
			console.log("💾 database persisted.")
		}

		return {
			init,
			waitForInitialisation,
			databaseName,
			reset,
			migrate,
			migrated,
			openingConnection,
			query,
			run,
			batchInsert,
			importJson,
			busy,
			persist,
		}
	},
	{
		persist: false,
	}
)
